import {
    faCheckCircle,
    faChevronDown,
    faChevronUp,
    faCircle,
    faHospital,
    faHourglassStart,
    faRefresh
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { callDashboardLLM, callGetDashboardLLMStatus } from "../../../amplify/appsync/dashboard-llm";
import { callQuery, LLM_VISIT_FEEDBACK_BY_CSN, LLMVisitFeedback, PatientFileVersion } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { callWrite, EXPLAIN_REVIEW_REVISIT, markJobAsViewed, SCAN_REVISIT, UPDATE_LLM_VISIT_FEEDBACK } from "../../../amplify/appsync/write";
import { GenerateModuleHeroV2 } from "../../common/GenerateModuleHeroV2";
import { dashboardLLMCache } from "../../dashboard/llm/cache";
import { FakeLoadingBar } from "../../dashboard/llm/common/FakeLoadingBar";
import { LLMDashboardUpdated } from "../../dashboard/llm/common/LLMDashboardUpdated";
import { sleep } from "../../dashboard/llm/common/utils";
import { isSectionUnread } from "../../dashboard/llm/utils";
import CustomSurveyForm, { getCombinedSectionToRate } from "./CustomSurveyForm";
import { NotableLabelsBadge } from "../../common/NotableLabelsBadge";
import { getNotableLabels, isFieldValueNotable } from "../../../utils/notableLabels";

interface DashboardProps {
    patientVisit?: PatientVisit;
    pastPatientVisits?: PatientVisit[];
    statuses: any;
    setStatuses: any;
    latestFileVersions: any;
    handleFileVersionChange: any;
    unreadFileVersionMap?: Record<string, PatientFileVersion>;
    className?: string;
    tenant?: string;
    isQuickView?: boolean;
    surveyResultsUpdateCallback?: (csn: string, results: Record<string, any>) => void;
    notableLabelsUpdateCallback?: (csn: string, surveyType: string, count: number) => void;
}

const EXPECTED_REFRESH_TIME = 120_000;

export const FEEDBACK_TYPE_INDEX_SUMMARY = "cohort_revisit_index_summary";
export const FEEDBACK_TYPE_REVISIT_SUMMARY = "cohort_revisit_revisit_summary";
export const FEEDBACK_TYPE_PATIENT_SUMMARY_REVISIT_SUMMARY = "cohort_revisit_patient_summary_revisit";
export const FEEDBACK_TYPE_PREDICTABILITY = "cohort_revisit_predictability";
export const FEEDBACK_TYPE_REASONING_PREDICTABILITY = "cohort_revisit_reasoning_predictability";
export const FEEDBACK_TYPE_PREDICTABILITY_THEMES = "cohort_revisit_predictability_themes";
export const FEEDBACK_TYPE_PREVENTABILITY = "cohort_revisit_preventability";
export const FEEDBACK_TYPE_REASONING_PREVENTABILITY = "cohort_revisit_reasoning_preventability";
export const FEEDBACK_TYPE_PREVENTABILITY_THEMES = "cohort_revisit_preventability_themes";
export const FEEDBACK_TYPE_POST_DISCHARGE_MONITORING = "cohort_revisit_post_discharge_monitoring";
export const FEEDBACK_TYPE_REASONING_PDM = "cohort_revisit_reasoning_pdm";
export const FEEDBACK_TYPE_DCI = "cohort_revisit_dci";
export const FEEDBACK_TYPE_REASONING_DCI = "cohort_revisit_reasoning_dci";
export const FEEDBACK_TYPE_NYU_CLASSIFICATION = "cohort_revisit_nyu_classification";
export const FEEDBACK_TYPE_REASONING_NYU = "cohort_revisit_reasoning_nyu";
export const FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_REVISIT = "cohort_revisit_longitudinal_care_insight_revisit";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_REVISIT = "cohort_revisit_therapeutic_pivot_revisit";
export const FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_REVISIT = "cohort_revisit_therapeutic_pivot_justification_revisit";
export const FEEDBACK_TYPE_VALUE_OF_CARE_REVISIT = "cohort_revisit_value_of_care_revisit";
export const FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_REVISIT = "cohort_revisit_value_of_care_justification_revisit";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_REVISIT = "cohort_revisit_astute_diagnosis_revisit";
export const FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_REVISIT = "cohort_revisit_astute_diagnosis_justification_revisit";

const REQUIRED_FEEDBACK_TYPES = [
    FEEDBACK_TYPE_INDEX_SUMMARY,
    FEEDBACK_TYPE_REVISIT_SUMMARY,
    FEEDBACK_TYPE_PATIENT_SUMMARY_REVISIT_SUMMARY,
    FEEDBACK_TYPE_PREDICTABILITY,
    FEEDBACK_TYPE_REASONING_PREDICTABILITY,
    FEEDBACK_TYPE_PREDICTABILITY_THEMES,
    FEEDBACK_TYPE_PREVENTABILITY,
    FEEDBACK_TYPE_REASONING_PREVENTABILITY,
    FEEDBACK_TYPE_PREVENTABILITY_THEMES,
    FEEDBACK_TYPE_POST_DISCHARGE_MONITORING,
    FEEDBACK_TYPE_REASONING_PDM,
    FEEDBACK_TYPE_DCI,
    FEEDBACK_TYPE_REASONING_DCI,
    FEEDBACK_TYPE_NYU_CLASSIFICATION,
    FEEDBACK_TYPE_REASONING_NYU,
    FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_REVISIT,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_REVISIT,
    FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_REVISIT,
    FEEDBACK_TYPE_VALUE_OF_CARE_REVISIT,
    FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_REVISIT,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_REVISIT,
    FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_REVISIT,
] as const;

const isSurveyCompleted = (feedbackTargetToSurvey: any) => {
    return REQUIRED_FEEDBACK_TYPES.every(type => feedbackTargetToSurvey[type]);
};


export const ExplainRevisitDashboard = ({
    patientVisit,
    pastPatientVisits,
    statuses,
    setStatuses,
    latestFileVersions = undefined,
    handleFileVersionChange = undefined,
    unreadFileVersionMap,
    className,
    isQuickView,
    tenant = 'stanford',
    surveyResultsUpdateCallback,
    notableLabelsUpdateCallback,
}: DashboardProps) => {

    const [isLoading, setIsLoading] = useState(false);
    const [scanRevisitData, setScanRevisitData] = useState<any>();
    const [explainReviewRevisit, setExplainReviewRevisit] = useState<any>();
    const [feedbackTargetToSurvey, setFeedbackTargetToSurvey] = useState<any>({});
    const [errorMessage, setErrorMessage] = useState("");
    const [isExpanded, setIsExpanded] = useState(true);
    const [manuallyChangedIsExpanded, setManuallyChangedIsExpanded] = useState(false);

    const sectionToSetter = {
        [EXPLAIN_REVIEW_REVISIT]: setExplainReviewRevisit,
    }

    // Function to fetch dashboard data
    const fetchDashboardData = async (patient: PatientVisit) => {
        setIsLoading(true);
        
        try {
            // We will render the data just like any other dashboard
            const cachedScanData = await dashboardLLMCache.get(SCAN_REVISIT, patient, tenant);
            if (cachedScanData) {
                setScanRevisitData(cachedScanData);
            } else {
                setScanRevisitData(undefined);
            }
            const cachedData = await dashboardLLMCache.get(EXPLAIN_REVIEW_REVISIT, patient, tenant);
            if (cachedData) {
                // Found cached data
                setExplainReviewRevisit(cachedData);
                markJobAsViewed(EXPLAIN_REVIEW_REVISIT, patientVisit);
                // Update notable labels count
                if (notableLabelsUpdateCallback && patientVisit) {
                    const notableCount = getNotableLabels('explain_review_revisit', cachedData).length;
                    notableLabelsUpdateCallback(patientVisit.csn, 'explain_review_revisit', notableCount);
                }
            } else {
                setExplainReviewRevisit(undefined);
            }
            setManuallyChangedIsExpanded(false);

            // Fetch survey data
            await fetchSurveyData();
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrorMessage("Could not load data. Check that you are on the Stanford network and if using VPN that your VPN Group is set to either 'CardinalKey-Full' or 'Stanford-Full'");

            setScanRevisitData(undefined);
            setExplainReviewRevisit(undefined);
        } finally {
            setIsLoading(false);
        }
    };
    
    // Function to fetch survey data
    const fetchSurveyData = async () => {
        if (!patientVisit) {
            return;
        }

        try {
            console.log("Fetching survey data for CSN:", patientVisit.csn);
            const feedbackItems = await callQuery({
                operation: LLM_VISIT_FEEDBACK_BY_CSN,
                csn: patientVisit.csn,
            }) as LLMVisitFeedback[];

            console.log(`Found ${feedbackItems?.length || 0} feedback items for CSN ${patientVisit.csn}`);

            if (feedbackItems && feedbackItems.length > 0) {
                // Get all original survey feedback items
                const originalSurveyFeedbackItems = feedbackItems.filter(
                    (item: LLMVisitFeedback) =>
                        item.feedback_type === 'survey' &&
                        (item.feedback_target.startsWith("cohort_revisit_"))
                );

                console.log(`Found ${originalSurveyFeedbackItems.length} revisit survey items for CSN ${patientVisit.csn}`);

                const feedbackTargetToSurvey: any = {};

                for (let item of originalSurveyFeedbackItems) {
                    const survey = JSON.parse(item.note);
                    feedbackTargetToSurvey[item.feedback_target] = {
                        "option": survey.option,
                        "options": survey.options,
                        "comment": survey.comment,
                    };
                }

                if (surveyResultsUpdateCallback) {
                    surveyResultsUpdateCallback(patientVisit.csn, feedbackTargetToSurvey);
                }

                setFeedbackTargetToSurvey(feedbackTargetToSurvey);
            } else {
                // No feedback items, clear the state
                console.log(`No feedback items found for CSN ${patientVisit.csn}, clearing survey state`);
                setFeedbackTargetToSurvey({});
            }
        } catch (error) {
            console.error("Error fetching survey data:", error);
        }
    };

    const handleSave = async (feedback_target: string, data: any): Promise<boolean> => {
        if (!patientVisit) {
            return false;
        }

        // Set this flag to prevent section from collapsing when user finishes survey
        setManuallyChangedIsExpanded(true);

        try {
            const response = await callWrite({
                operation: UPDATE_LLM_VISIT_FEEDBACK,
                creation_time: new Date().getTime(),
                csn: patientVisit.csn,
                mrn: '',
                feedback_target: feedback_target,
                feedback_type: 'survey',
                note: JSON.stringify(data),
            });
            
            // Check if response is empty or has an error
            if (!response || Object.keys(response).length === 0) {
                window.alert("Failed to submit survey. Please refresh and try again.");
                return false;
            }

            // Refresh the survey data after submission
            await fetchSurveyData();
            
            return true;
        } catch (error) {
            console.error("Error submitting original survey:", error);
            
            // Show alert with error details
            if (error instanceof Error) {
                window.alert(`Failed to submit survey: ${error.message}`);
            } else {
                window.alert("Failed to submit survey. An unknown error occurred.");
            }

            return false;
        }
    };

    const getIsCompleted = () => {
        const isCompleted = isSurveyCompleted(feedbackTargetToSurvey);

        return (
            <Row>
                <Col>
                    {
                        isCompleted ? (
                            <Alert variant="success">
                                <span className="me-1"><FontAwesomeIcon icon={faCheckCircle} /></span> Survey Completed
                            </Alert>
                        ) : (
                            <Alert variant="info">
                                <span className="me-1"><FontAwesomeIcon icon={faHourglassStart} /></span> Survey Not Yet Completed
                            </Alert>
                        )
                    }
                </Col>
            </Row>
        );
    };

    const getContextSection = (title: string, data: string | string[], horizontalLineAbove: boolean = true) => {
        return (
            <>
                {horizontalLineAbove && <hr />}
                <Row className="mb-2">
                    <Col>
                        <strong>{title}</strong>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {data}
                            </div>
                        )}
                    </Col>
                </Row>
            </>
        );
    }

    const getSectionToRate = (title: string, data: string, surveyTitle: string,
        surveyDescription: string | undefined, surveyOptions: string[],
        surveyKey: string, isMultiSelect?: boolean, optionToValueMap?: any, useSegmentedButtons?: boolean,
        customLeftHeader?: string, customRightHeader?: string) => {
        return (
            <>
                {/* Question Header spanning both columns with box */}
                <Row className="mt-3">
                    <Col>
                        <div className="survey-question-header mb-2">
                            <div>
                                {title}
                            </div>
                            {surveyDescription && (
                                <div className="small mt-1" style={{fontWeight: 'normal'}}>
                                    {surveyDescription}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
                {/* Column Headers */}
                <Row className="mb-2">
                    <Col>
                        <div className="text-muted small">
                            <strong>{customLeftHeader || "Capacity Assessment"}</strong>
                        </div>
                    </Col>
                    <Col>
                        <div className="text-muted small">
                            <strong>{customRightHeader || "MD Assessment"}</strong>
                        </div>
                    </Col>
                </Row>
                {/* Content Row */}
                <Row>
                    <Col>
                        {Array.isArray(data) ? (
                            <div className="survey-context">
                                <ul>
                                    {data.map((item, index) => (
                                        <li key={index}>
                                            {(optionToValueMap && optionToValueMap[item]) ? optionToValueMap[item] : item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="survey-context">
                                {(optionToValueMap && optionToValueMap[data]) ? optionToValueMap[data] : data}
                            </div>
                        )}
                    </Col>
                    <Col>
                        <CustomSurveyForm
                            title={surveyTitle}
                            altTitle={title}
                            description={undefined}
                            options={surveyOptions}
                            onSubmit={(data) => handleSave(surveyKey, data)}
                            enableComment={true}
                            initialSelectedOption={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].option : undefined}
                            initialSelectedOptions={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].options : undefined}
                            initialComment={feedbackTargetToSurvey[surveyKey] ? feedbackTargetToSurvey[surveyKey].comment : undefined}
                            isMultiSelect={isMultiSelect}
                            optionToValueMap={optionToValueMap}
                            useSegmentedButtons={useSegmentedButtons}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    const flattenAndFormatNotes = (notes: any) => {
        let output = "";
        for (let k of Object.keys(notes)) {
            for (let note of notes[k]) {
                output += `NOTE ${(note.note_text)}\n`
                output += note.note_text + "\n\n";
            }
        }
        return output;
    }

    function checkVisitCriteria(visits: PatientVisit[], csn: string): { result: boolean; reason?: string } {
        // Find the target visit
        const targetVisit = visits.find(v => v.csn === csn);
        if (!targetVisit) {
            return { result: false, reason: "No visit found with the provided CSN." };
        }

        // Check if target visit has ed_dispo set
        if (!targetVisit.ed_dispo || targetVisit.ed_dispo.trim() === "") {
            return { result: false, reason: "Target visit does not have ed_dispo set." };
        }

        const targetArrival = new Date(targetVisit.arrival_time);
        const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

        // Look for a previous visit within 30 days of the target's arrival
        const hasPrevVisit = visits.some(v => {
            const arrival = new Date(v.arrival_time);
            return (
                arrival < targetArrival &&
                targetArrival.getTime() - arrival.getTime() <= THIRTY_DAYS_MS
            );
        });

        if (!hasPrevVisit) {
            return { result: false, reason: "No previous visit within 30 days of target visit." };
        }

        return { result: true };
    }

    // Update useEffect to also fetch patient visit data
    useEffect(() => {
        if (patientVisit) {
            fetchDashboardData(patientVisit);
        }
    }, [patientVisit, tenant]);

    useEffect(() => {
        if (isQuickView && !manuallyChangedIsExpanded) {
            if (isSurveyCompleted(feedbackTargetToSurvey)) {
                setIsExpanded(false);
            } else {
                setIsExpanded(true);
            }
        }
    }, [feedbackTargetToSurvey]);

    const refresh = (refresh: boolean) => {
        if (!patientVisit) {
            return;
        }
        setIsLoading(true);
        callDashboardLLM({
            "operation": SCAN_REVISIT,
            "mrn": patientVisit.mrn,
            "csn": patientVisit.csn,
        }).then((scanRevisitResponse) => {
            setScanRevisitData(scanRevisitResponse);
            const startTime = new Date().toISOString().split('.')[0] + 'Z';
            callWrite({
                operation: EXPLAIN_REVIEW_REVISIT,
                mrn: patientVisit.mrn,
                csn: patientVisit.csn,
                report_date_str: new Date().toISOString().split('.')[0] + 'Z',
            }).then((reviewRevisitResponse) => {
                pollUntilDone({
                    operation: EXPLAIN_REVIEW_REVISIT, 
                    startTime,
                });
            }).catch(() => {
                setIsLoading(false);
            });
        }).catch(() => {
            setIsLoading(false);
        });
    };

    const pollUntilDone = async ({
        operation,
        startTime,
        delayMs = 2000,
        maxAttempts = 60,
    }: any) => {
        if (patientVisit) {
            for (let attempt = 0; attempt < maxAttempts; attempt++) {
                const statuses = await callGetDashboardLLMStatus(patientVisit.csn);
                if (statuses[operation] && statuses[operation].lock_timestamp === null && statuses[operation].last_sync_timestamp > startTime) {
                    const cachedCurrentVisit = await dashboardLLMCache.get(operation, patientVisit, tenant);
                    if (cachedCurrentVisit) {
                        setExplainReviewRevisit(cachedCurrentVisit);
                        markJobAsViewed(EXPLAIN_REVIEW_REVISIT, patientVisit);
                    }

                    const cachedScanData = await dashboardLLMCache.get(SCAN_REVISIT, patientVisit, tenant);
                    if (cachedScanData) {
                        setScanRevisitData(cachedScanData);
                    }
                    setIsLoading(false);
                    setStatuses(statuses);
                    return;
                }

                await sleep(delayMs);
            }
            setIsLoading(false);
            console.error('Polling timed out');
        }
    };

    if ((!isQuickView && !pastPatientVisits) || !patientVisit) {
        return <></>
    }

    if (isQuickView && (!scanRevisitData || Object.keys(scanRevisitData).length === 0 || !explainReviewRevisit || Object.keys(explainReviewRevisit).length === 0)) {
        return <></>
    }

    // Quick view doesn't have the option to generate this section
    if (pastPatientVisits) {
        const criteria = checkVisitCriteria(pastPatientVisits, patientVisit.csn);
        if (!criteria.result) {
            return (
                <Card className="mb-3">
                    <Card.Body>
                        <Row className="mb-3">
                            <Col md={12} className="text-left">
                                <div className="d-flex align-items-center">
                                    <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span>

                                    <strong>ED Revisits Review - Predictability & Preventability Assessment</strong>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                This patient is ineligible for review/assessment - {criteria.reason}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )
        }
    }

    if (!isLoading && (!explainReviewRevisit || explainReviewRevisit["error"])) {
        return (
            <>
                <GenerateModuleHeroV2 type={EXPLAIN_REVIEW_REVISIT} setRegenerateTarget={() => {}} setShowContextMenu={refresh} />
            </>
        )
    }

    const missingItems = [];

    if (!scanRevisitData) {
        missingItems.push("Revisit Data");
    } else {
        if (!scanRevisitData.index_formatted_notes?.length) {
            missingItems.push("Index ED Note");
        }
        if (!scanRevisitData.index_dc_note?.note_text) {
            missingItems.push("Index DC Instructions");
        }
        if (!scanRevisitData.revisit_formatted_notes?.length) {
            missingItems.push("Revisit ED Note");
        }
    }

    return (
        <Card className={`fade-in ${className} mb-3`}>
            <FakeLoadingBar isActive={isLoading} maxDuration={EXPECTED_REFRESH_TIME} />
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <div className="d-flex align-items-center">
                            <span className={`dashboard-actions-circle-small dashboard-actions-circle-blue me-2`}><FontAwesomeIcon icon={faHospital} fixedWidth /></span><strong>Revisit Assessment</strong> {isSectionUnread(EXPLAIN_REVIEW_REVISIT, unreadFileVersionMap) && <Badge bg="info" className="ms-2">NEW</Badge>} {isSurveyCompleted(feedbackTargetToSurvey) && <Badge bg="success" className="ms-2">COMPLETED</Badge>} {explainReviewRevisit && <NotableLabelsBadge count={getNotableLabels('explain_review_revisit', explainReviewRevisit).length} labels={getNotableLabels('explain_review_revisit', explainReviewRevisit)} />}
                        </div>
                    </Col>
                    <Col md={6} className="text-right">
                        {patientVisit && <LLMDashboardUpdated patient={patientVisit} statuses={statuses} sectionToSetter={sectionToSetter} latestFileVersions={latestFileVersions} explainKey={EXPLAIN_REVIEW_REVISIT} handleFileVersionChange={handleFileVersionChange} />}
                    
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Refresh Section
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="primary"
                                className="dashboard-round-btn"
                                onClick={(e: any) => {
                                    e.stopPropagation();
                                    refresh(true);
                                }}
                                disabled={isLoading}
                            >
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </OverlayTrigger>
                        <Button
                            variant="primary"
                            className="dashboard-round-btn"
                            onClick={(e: any) => {e.stopPropagation(); setIsExpanded(!isExpanded); setManuallyChangedIsExpanded(true)}}
                        >
                            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                    </Col>
                </Row>
                {isLoading && (
                        <div className='mt-2'>
                            <FontAwesomeIcon icon={faCircle} className="pulsating-dot" />
                        </div>
                    )
                }
                {!isLoading && isExpanded && scanRevisitData && scanRevisitData.index_formatted_notes && scanRevisitData.index_dc_note && scanRevisitData.revisit_formatted_notes && explainReviewRevisit ? (
                    <Container>
                        {getIsCompleted()}
                        {getContextSection("Index ED Note", flattenAndFormatNotes(scanRevisitData.index_formatted_notes), false)}
                        {getContextSection("Index DC Instructions", scanRevisitData.index_dc_note?.note_text)}
                        {getContextSection("Revisit ED Note", flattenAndFormatNotes(scanRevisitData.revisit_formatted_notes))}
                        {getSectionToRate("Index Visit Summary", explainReviewRevisit.index_summary, "",
                            undefined,
                            ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_INDEX_SUMMARY, false, undefined, true,
                            "Capacity Summary", "MD Rating of Capacity Summary")}

                        <Row className="mt-3">
                            <Col>
                                <div className="survey-question-header mb-2">
                                    <div>Index Diagnosis</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {Array.isArray(scanRevisitData.dx_index) ? (
                                    <div className="survey-context">
                                        <ul>
                                            {scanRevisitData.dx_index.map((item: string, index: number) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="survey-context">
                                        {scanRevisitData.dx_index}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col>
                                <div className="survey-question-header mb-2">
                                    <div>Days Between Visits</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="survey-context">
                                    {scanRevisitData.days_between_visits}
                                </div>
                            </Col>
                        </Row>
                        {getSectionToRate("Revisit Summary", explainReviewRevisit.revisit_summary, "",
                            undefined,
                            ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_REVISIT_SUMMARY, false, undefined, true,
                            "Capacity Summary", "MD Rating of Capacity Summary")}
                        {getSectionToRate("Revisit Summary (written for Patient)", explainReviewRevisit.patient_summary_revisit, "",
                            undefined,
                            ['Accurate', 'Partially accurate', 'Inaccurate'], FEEDBACK_TYPE_PATIENT_SUMMARY_REVISIT_SUMMARY, false, undefined, true,
                            "Capacity Summary", "MD Rating of Capacity Summary")}

                        <Row className="mt-3">
                            <Col>
                                <div className="survey-question-header mb-2">
                                    <div>Revisit Diagnosis</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {Array.isArray(scanRevisitData.dx_revisit) ? (
                                    <div className="survey-context">
                                        <ul>
                                            {scanRevisitData.dx_revisit.map((item: string, index: number) => (
                                                <li key={index}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div className="survey-context">
                                        {scanRevisitData.dx_revisit}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col>
                                <div className="survey-question-header mb-2">
                                    <div>Revisit Disposition</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className="survey-context">
                                    {scanRevisitData.dispo_class_adt_revisit}
                                </div>
                            </Col>
                        </Row>

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Predictability of Revisit",
                            explainReviewRevisit.predictability,
                            explainReviewRevisit.reasoning_predictability,
                            false,
                            "Was the revisit for a new problem, or could it have been predicted from the index visit?",
                            ['Very likely predictable', 'Likely Predictable', 'Possibly Predictable', 'Unlikely Predictable', 'Very Unlikely Predictable'],
                            FEEDBACK_TYPE_PREDICTABILITY,
                            "Reasoning Quality",
                            "Quality of reasoning:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_REASONING_PREDICTABILITY,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_revisit', 'predictability', explainReviewRevisit.predictability)
                        )}

                        {getSectionToRate(
                            "Predictability Themes",
                            explainReviewRevisit.predictability_themes,
                            "",
                            "Relevant themes that contributed to the predictability of this revisit.",
                            [
                                'Unstable chronic disease',
                                'Uncontrolled symptoms at index discharge',
                                'Known adherence barriers',
                                'Incomplete workup or management at index visit',
                                'Revisit for adverse effects of new therapy',
                                'Revisit for planned follow up'
                            ],
                            FEEDBACK_TYPE_PREDICTABILITY_THEMES,
                            true,
                            {
                                'unstable_chronic_disease': 'Unstable chronic disease',
                                'uncontrolled_sxs_at_dc': 'Uncontrolled symptoms at index discharge',
                                'adherence_barriers': 'Known adherence barriers',
                                'incomplete_workup_mgmt': 'Incomplete workup or management at index visit',
                                'adverse_effects': 'Revisit for adverse effects of new therapy',
                                'planned_fup': 'Revisit for planned follow up',
                                'high_risk_pt': 'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                                'sdoh': 'Other'
                            }
                        )}
                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Preventability of Revisit",
                            explainReviewRevisit.preventability,
                            explainReviewRevisit.reasoning_preventability,
                            false,
                            "Could clinical or social factors, if addressed differently, have reasonably prevented the revisit?",
                            [
                                'Very likely preventable',
                                'Likely preventable',
                                'Possibly preventable',
                                'Unlikely preventable',
                                'Very unlikely preventable'
                            ],
                            FEEDBACK_TYPE_PREVENTABILITY,
                            "Reasoning Quality",
                            "Quality of reasoning:",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_REASONING_PREVENTABILITY,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_revisit', 'preventability', explainReviewRevisit.preventability)
                        )}

                        {getSectionToRate(
                            "Preventability Themes",
                            explainReviewRevisit.preventability_themes,
                            "",
                            "Relevant themes that contributed to the preventability of this revisit.", 
                            [
                                'Inadequate DC therapeutic regimen',
                                'Inadequate DC guidance on expected symptoms/course',
                                'Unaddressed social determinants of health (e.g. homelessness, transportation)',
                                'Inadequate outpatient follow-up plan',
                                'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                                'Incomplete ED diagnosis or management'
                            ],
                            FEEDBACK_TYPE_PREVENTABILITY_THEMES,
                            true,
                            {
                                'dc_meds': 'Inadequate DC therapeutic regimen',
                                'dc_guidance': 'Inadequate DC guidance on expected symptoms/course',
                                'uncontrolled_sxs_at_dc': 'Unaddressed social determinants of health (e.g. homelessness, transportation)',
                                'outpatient_coordination': 'Inadequate outpatient follow-up plan',
                                'high_risk_pt': 'High risk patient (e.g. frail, elderly with chronic high risk condition)',
                                'incomplete_ed_dx_rx': 'Incomplete ED diagnosis or management',
                                'unstable_chronic_disease': 'Unstable chronic disease',
                                'adherence_barriers': 'Known adherence barriers',
                                'adverse_effects': 'Revisit for adverse effects of new therapy',
                                'sdoh': 'Other'
                            }
                        )}
                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Post-Discharge of Revisit",
                            explainReviewRevisit.post_discharge_monitoring,
                            explainReviewRevisit.reasoning_pdm,
                            false,
                            "Could the revisit have been better predicted or prevented with appropriate post-discharge monitoring? Please rate the relevance of post-discharge monitoring for this revisit.",
                            [
                                'Very likely helpful',
                                'Likely helpful',
                                'Possibly helpful',
                                'Unlikely helpful',
                                'Very unlikely helpful'
                            ],
                            FEEDBACK_TYPE_POST_DISCHARGE_MONITORING,
                            "Reasoning Quality",
                            "Please rate the quality of the reasoning provided for the relevance of post-discharge monitoring.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_REASONING_PDM,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_revisit', 'post_discharge_monitoring', explainReviewRevisit.post_discharge_monitoring)
                        )}
                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Discharge Instructions",
                            explainReviewRevisit.dci,
                            explainReviewRevisit.reasoning_dci,
                            false,
                            "Could the revisit have been prevented with better DC instructions, including interactive/longitudinal interaction after the index visit? Please rate the relevance of DC instructions and follow-up.",
                            [
                                'Very likely helpful',
                                'Likely helpful',
                                'Possibly helpful',
                                'Unlikely helpful',
                                'Very unlikely helpful'
                            ],
                            FEEDBACK_TYPE_DCI,
                            "Reasoning Quality",
                            "Please rate the quality of the reasoning provided for the relevance of DC instructions.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_REASONING_DCI,
                            false,
                            undefined,
                            isFieldValueNotable('explain_review_revisit', 'dci', explainReviewRevisit.dci)
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "NYU Classification", 
                            explainReviewRevisit.nyu_classification,
                            explainReviewRevisit.reasoning_nyu,
                            true,
                            "Please select the most appropriate NYU ED visit classification for this revisit.", 
                            [
                                'Non-Emergent',
                                'Emergent Primary Care Treatable',
                                'Emergent ED Care Needed (Preventable)',
                                'Emergent ED Care Needed (Non-Preventable)',
                            ],
                            FEEDBACK_TYPE_NYU_CLASSIFICATION,
                            "Reasoning Quality",
                            "Please rate the quality of the reasoning provided for why this NYU classification was chosen.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_REASONING_NYU,
                            false,
                            {
                                'non_emergent': 'Non-Emergent',
                                'emergent_primary_care_treatable': 'Emergent Primary Care Treatable',
                                'emergent_ed_care_needed_preventable_avoidable': 'Emergent ED Care Needed (Preventable)',
                                'emergent_ed_care_needed_not_preventable_avoidable': 'Emergent ED Care Needed (Non-Preventable)',
                            }
                        )}

                        {getSectionToRate(
                            "Longitudinal Care Insight", 
                            explainReviewRevisit.longitudinal_care_insight_revisit, 
                            "Clinical Pearl / Longitudinal Care Insight", 
                            "Please rate the quality of the longitudinal care insight provided for future providers.", 
                            ['Good', 'Incomplete', 'Poor'], 
                            FEEDBACK_TYPE_LONGITUDINAL_CARE_INSIGHT_REVISIT
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Therapeutic Pivot", 
                            explainReviewRevisit.therapeutic_pivot_revisit,
                            explainReviewRevisit.therapeutic_pivot_justification_revisit,
                            false,
                            "Was there a significant change in treatment plan during the revisit ED stay?", 
                            ['yes', 'possibly', 'no'], 
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_REVISIT,
                            "Reasoning Quality for Therapeutic Pivot",
                            "Please rate the quality of the justification for the therapeutic pivot.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_THERAPEUTIC_PIVOT_JUSTIFICATION_REVISIT
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Value of Care", 
                            explainReviewRevisit.value_of_care_revisit,
                            explainReviewRevisit.value_of_care_justification_revisit,
                            false,
                            "Please rate the value added by the revisit ED encounter.", 
                            ['high', 'moderate', 'low', 'negative'], 
                            FEEDBACK_TYPE_VALUE_OF_CARE_REVISIT,
                            "Reasoning Quality for Value of Care",
                            "Please rate the quality of the justification provided for the value of care rating.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_VALUE_OF_CARE_JUSTIFICATION_REVISIT
                        )}

                        {getCombinedSectionToRate(handleSave, feedbackTargetToSurvey,
                            "Astute Diagnosis", 
                            explainReviewRevisit.astute_diagnosis_revisit,
                            explainReviewRevisit.astute_diagnosis_justification_revisit,
                            false,
                            "Was a correct and high-impact diagnosis made that was not obvious or routine?", 
                            ['yes', 'possibly', 'no'], 
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_REVISIT,
                            "Reasoning Quality for Astute Diagnosis",
                            "Please rate the quality of the justification provided for why this diagnosis was considered astute.",
                            ['Good', 'Incomplete', 'Poor'],
                            FEEDBACK_TYPE_ASTUTE_DIAGNOSIS_JUSTIFICATION_REVISIT
                        )}

                    </Container>
                ) : !isLoading && isExpanded && missingItems.length > 0 ? (
                    <>We couldn't generate an assessment because the following information is missing: <strong>{missingItems.join(", ")}</strong></>
                ) : !isLoading && isExpanded && scanRevisitData ? (
                    <>We're sorry - we could not generate an assessment due to an error</>
                ) : (<></>)
                }
            </Card.Body>
        </Card>
    );
};
